import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { parseLocationString } from "@/lib/locationUtils";
import {
  isPhoneValidForNearMeWhatsapp,
  normalizeProfilePhoneDigits,
} from "@/lib/notifications/near-me-whatsapp-preference-sync";
import type { NearMeGranularity } from "@/lib/push-notifications/location-matching";

export type NearMeDefaultGranularity = NearMeGranularity;

export type NearMeDefaultDeliveryMode = "realtime" | "digest_daily";

export type NearMeUserDefaultsPolicy = {
  near_me_default_granularity: NearMeDefaultGranularity;
  near_me_default_delivery_mode_state: NearMeDefaultDeliveryMode;
  near_me_default_delivery_mode_lga: NearMeDefaultDeliveryMode;
  near_me_default_delivery_mode_neighborhood: NearMeDefaultDeliveryMode;
};

/** Launch defaults seeded in migration and used when columns are null. */
export const LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY: NearMeUserDefaultsPolicy = {
  near_me_default_granularity: "state",
  near_me_default_delivery_mode_state: "digest_daily",
  near_me_default_delivery_mode_lga: "realtime",
  near_me_default_delivery_mode_neighborhood: "realtime",
};

const GRANULARITY_VALUES: NearMeDefaultGranularity[] = [
  "state",
  "lga",
  "neighborhood",
];

const DELIVERY_MODE_VALUES: NearMeDefaultDeliveryMode[] = [
  "realtime",
  "digest_daily",
];

function isNearMeDefaultGranularity(
  value: unknown,
): value is NearMeDefaultGranularity {
  return (
    typeof value === "string" &&
    (GRANULARITY_VALUES as readonly string[]).includes(value)
  );
}

function isNearMeDefaultDeliveryMode(
  value: unknown,
): value is NearMeDefaultDeliveryMode {
  return (
    typeof value === "string" &&
    (DELIVERY_MODE_VALUES as readonly string[]).includes(value)
  );
}

type SystemSettingsNearMeDefaultsRow = Pick<
  Database["public"]["Tables"]["system_settings"]["Row"],
  | "near_me_default_granularity"
  | "near_me_default_delivery_mode_state"
  | "near_me_default_delivery_mode_lga"
  | "near_me_default_delivery_mode_neighborhood"
>;

export function mergeNearMeUserDefaultsPolicyFromRow(
  row: Partial<SystemSettingsNearMeDefaultsRow> | null | undefined,
): NearMeUserDefaultsPolicy {
  const launch = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY;
  if (!row) return { ...launch };

  return {
    near_me_default_granularity: isNearMeDefaultGranularity(
      row.near_me_default_granularity,
    )
      ? row.near_me_default_granularity
      : launch.near_me_default_granularity,
    near_me_default_delivery_mode_state: isNearMeDefaultDeliveryMode(
      row.near_me_default_delivery_mode_state,
    )
      ? row.near_me_default_delivery_mode_state
      : launch.near_me_default_delivery_mode_state,
    near_me_default_delivery_mode_lga: isNearMeDefaultDeliveryMode(
      row.near_me_default_delivery_mode_lga,
    )
      ? row.near_me_default_delivery_mode_lga
      : launch.near_me_default_delivery_mode_lga,
    near_me_default_delivery_mode_neighborhood: isNearMeDefaultDeliveryMode(
      row.near_me_default_delivery_mode_neighborhood,
    )
      ? row.near_me_default_delivery_mode_neighborhood
      : launch.near_me_default_delivery_mode_neighborhood,
  };
}

export function recommendedNearMeDeliveryMode(
  granularity: NearMeDefaultGranularity,
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
): NearMeDefaultDeliveryMode {
  switch (granularity) {
    case "state":
      return policy.near_me_default_delivery_mode_state;
    case "lga":
      return policy.near_me_default_delivery_mode_lga;
    case "neighborhood":
      return policy.near_me_default_delivery_mode_neighborhood;
    default: {
      const _exhaustive: never = granularity;
      return _exhaustive;
    }
  }
}

/** Growth-phase backfill timestamp; prefs updated before this may be migrated to digest (Phase 3). */
export const GROWTH_PHASE_NEAR_ME_BACKFILL_CUTOFF_ISO =
  "2026-05-30T00:00:00.000Z";

export function isNearMeDeliveryModeRecommended(
  granularity: NearMeDefaultGranularity,
  deliveryMode: NearMeDefaultDeliveryMode,
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
): boolean {
  return recommendedNearMeDeliveryMode(granularity, policy) === deliveryMode;
}

export type NearMeGranularityChangeDeliveryResult =
  | {
      kind: "auto";
      deliveryMode: NearMeDefaultDeliveryMode;
    }
  | {
      kind: "confirm";
      previousGranularity: NearMeDefaultGranularity;
      newGranularity: NearMeDefaultGranularity;
      recommendedDelivery: NearMeDefaultDeliveryMode;
    };

/**
 * When the user changes geography tier, auto-align delivery if they were on the
 * previous tier's admin recommendation; otherwise ask before changing delivery.
 */
export function resolveNearMeDeliveryOnGranularityChange(
  previousGranularity: NearMeDefaultGranularity,
  newGranularity: NearMeDefaultGranularity,
  currentDeliveryMode: NearMeDefaultDeliveryMode,
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
): NearMeGranularityChangeDeliveryResult {
  const previousRecommended = recommendedNearMeDeliveryMode(
    previousGranularity,
    policy,
  );
  const newRecommended = recommendedNearMeDeliveryMode(newGranularity, policy);

  if (currentDeliveryMode === previousRecommended) {
    return { kind: "auto", deliveryMode: newRecommended };
  }

  return {
    kind: "confirm",
    previousGranularity,
    newGranularity,
    recommendedDelivery: newRecommended,
  };
}

export function profileStateDisplayName(
  location: string | null | undefined,
): string {
  const trimmed = location?.trim();
  if (!trimmed) return "your state";
  const parsed = parseLocationString(trimmed);
  return parsed.state?.trim() || "your state";
}

export function nearMeGranularityLabel(
  granularity: NearMeDefaultGranularity,
): string {
  switch (granularity) {
    case "state":
      return "state-wide";
    case "lga":
      return "your Local Government Area";
    case "neighborhood":
      return "neighborhood";
    default: {
      const _exhaustive: never = granularity;
      return _exhaustive;
    }
  }
}

export function formatNearMeDeliverySummary(options: {
  granularity: NearMeDefaultGranularity;
  deliveryMode: NearMeDefaultDeliveryMode;
  stateDisplayName?: string;
}): string | null {
  const {
    granularity,
    deliveryMode,
    stateDisplayName = "your state",
  } = options;

  if (granularity === "state" && deliveryMode === "digest_daily") {
    return `Daily summary for new listings in ${stateDisplayName}`;
  }

  if (granularity === "state" && deliveryMode === "realtime") {
    return `Alert as each listing is approved across ${stateDisplayName}`;
  }

  if (granularity === "lga" && deliveryMode === "realtime") {
    return "Alert when something new is listed in your Local Government Area";
  }

  if (granularity === "neighborhood" && deliveryMode === "realtime") {
    return "Alert when something new is listed in your neighborhood";
  }

  if (deliveryMode === "digest_daily") {
    return `Daily summary for new ${nearMeGranularityLabel(granularity)} matches`;
  }

  return null;
}

export function shouldWarnStateRealtimeVolume(
  granularity: NearMeDefaultGranularity,
  deliveryMode: NearMeDefaultDeliveryMode,
): boolean {
  return granularity === "state" && deliveryMode === "realtime";
}

export function nearMeComboDiffersFromAdminDefaults(
  granularity: NearMeDefaultGranularity,
  deliveryMode: NearMeDefaultDeliveryMode,
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
): boolean {
  const recommendedForTier = recommendedNearMeDeliveryMode(granularity, policy);
  return (
    granularity !== policy.near_me_default_granularity ||
    deliveryMode !== recommendedForTier
  );
}

export function adminDefaultDeliveryHint(
  granularity: NearMeDefaultGranularity,
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
): string {
  const recommended = recommendedNearMeDeliveryMode(granularity, policy);
  if (granularity === "state" && recommended === "digest_daily") {
    return "Platform default is a daily digest for state-wide alerts.";
  }
  if (recommended === "realtime") {
    return `Platform default is an alert as listings are approved for ${nearMeGranularityLabel(granularity)} matches.`;
  }
  return `Platform default is a daily digest for ${nearMeGranularityLabel(granularity)} matches.`;
}

export type NearMePreferencesFromDefaults = Pick<
  Database["public"]["Tables"]["notification_preferences"]["Insert"],
  | "notify_new_listings_near_me"
  | "new_listings_near_me_granularity"
  | "near_me_delivery_mode"
  | "near_me_notify_email"
  | "near_me_notify_whatsapp"
  | "near_me_whatsapp_opt_in_at"
  | "near_me_whatsapp_opt_out_at"
  | "near_me_categories"
  | "near_me_subcategories"
>;

/** Default WA near-me on at signup when profile phone passes validity bar. */
export function buildDefaultNearMeWhatsappConsentFields(options?: {
  phoneDigits?: string | null;
  nowIso?: string;
}): Pick<
  NearMePreferencesFromDefaults,
  | "near_me_notify_whatsapp"
  | "near_me_whatsapp_opt_in_at"
  | "near_me_whatsapp_opt_out_at"
> {
  const digits = normalizeProfilePhoneDigits(options?.phoneDigits ?? null);
  const defaultOn = isPhoneValidForNearMeWhatsapp(digits);
  if (!defaultOn) {
    return {
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_in_at: null,
      near_me_whatsapp_opt_out_at: null,
    };
  }

  return {
    near_me_notify_whatsapp: true,
    near_me_whatsapp_opt_in_at: options?.nowIso ?? new Date().toISOString(),
    near_me_whatsapp_opt_out_at: null,
  };
}

export function defaultNearMeWhatsappEnabledForPhone(
  phone: string | null | undefined,
): boolean {
  return isPhoneValidForNearMeWhatsapp(normalizeProfilePhoneDigits(phone));
}

/** Buyer-facing disclosure for signup / onboarding near phone field. */
export function nearMeWhatsappSignupDisclosureLine(
  phone: string | null | undefined,
): string {
  const digits = normalizeProfilePhoneDigits(phone);
  const display =
    digits && digits.length >= 10 ? `+${digits}` : "your WhatsApp number";
  return `We'll send nearby item alerts to ${display}. Turn off anytime in Profile → Notifications.`;
}

export function buildNearMePreferencesFromAdminDefaults(
  policy: NearMeUserDefaultsPolicy = LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
  options?: {
    nearMeEnabled?: boolean;
    phoneDigits?: string | null;
    nowIso?: string;
  },
): NearMePreferencesFromDefaults {
  const nearMeEnabled = options?.nearMeEnabled ?? true;

  if (!nearMeEnabled) {
    return {
      notify_new_listings_near_me: false,
      new_listings_near_me_granularity: null,
      near_me_delivery_mode: "realtime",
      near_me_notify_email: false,
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_in_at: null,
      near_me_whatsapp_opt_out_at: null,
      near_me_categories: null,
      near_me_subcategories: null,
    };
  }

  const granularity = policy.near_me_default_granularity;
  const whatsappConsent = buildDefaultNearMeWhatsappConsentFields({
    phoneDigits: options?.phoneDigits,
    nowIso: options?.nowIso,
  });

  return {
    notify_new_listings_near_me: true,
    new_listings_near_me_granularity: granularity,
    near_me_delivery_mode: recommendedNearMeDeliveryMode(granularity, policy),
    near_me_notify_email: true,
    ...whatsappConsent,
    near_me_categories: null,
    near_me_subcategories: null,
  };
}

export async function loadNearMeUserDefaultsPolicy(
  service: SupabaseClient<Database>,
): Promise<NearMeUserDefaultsPolicy> {
  const { data, error } = await service
    .from("system_settings")
    .select(
      "near_me_default_granularity, near_me_default_delivery_mode_state, near_me_default_delivery_mode_lga, near_me_default_delivery_mode_neighborhood",
    )
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    console.error("[near_me] system_settings defaults load:", error);
    return { ...LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY };
  }

  return mergeNearMeUserDefaultsPolicyFromRow(data);
}

export function validateNearMeUserDefaultsPolicyInput(
  input: Record<string, unknown>,
):
  | { ok: true; policy: Partial<NearMeUserDefaultsPolicy> }
  | { ok: false; error: string } {
  const policy: Partial<NearMeUserDefaultsPolicy> = {};

  if ("near_me_default_granularity" in input) {
    const value = input.near_me_default_granularity;
    if (!isNearMeDefaultGranularity(value)) {
      return {
        ok: false,
        error:
          "near_me_default_granularity must be state, lga, or neighborhood",
      };
    }
    policy.near_me_default_granularity = value;
  }

  for (const key of [
    "near_me_default_delivery_mode_state",
    "near_me_default_delivery_mode_lga",
    "near_me_default_delivery_mode_neighborhood",
  ] as const) {
    if (!(key in input)) continue;
    const value = input[key];
    if (!isNearMeDefaultDeliveryMode(value)) {
      return {
        ok: false,
        error: `${key} must be realtime or digest_daily`,
      };
    }
    policy[key] = value;
  }

  return { ok: true, policy };
}
